import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { green, grey, red } from '@material-ui/core/colors';
import { Comment, Flag, RemoveRedEye } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { Alert, Autocomplete, Skeleton } from '@material-ui/lab';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import classNames from 'classnames';
import { DatePicker, PageableTable } from 'components';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import { PageableTableHeader } from 'components/PageableTable/components';
import { DateTimeFormat } from 'config';
import {
  differenceInHours,
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useDebounce,
  useDocumentTitle,
  useLocalStorageSettings,
} from 'hooks';
import { fetchJobDetails } from 'lib/Api/Jobs';
import {
  exportNpsResponses,
  fetchNpsLeaders,
  fetchNpsResponses,
  fetchLastUpdated,
} from 'lib/Api/Nps';
import { getUserData } from 'lib/Helper/Auth';
import { Job } from 'lib/Model/Job';
import { NpsLeader, PagedNpsResponse, NpsLastUpdated } from 'lib/Model/Nps';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import JobDetailsDialog from 'views/Clients/components/JobDetailsDialog';
import { resolvePlatformName } from 'views/Clients/components/SuggestedMatches';
import { brandList, NpsDialog } from './NpsDialog';
import { TagsDialog } from './TagsDialog';
import LaunchIcon from '@material-ui/icons/Launch';
import { CustomTooltip } from 'components/CustomTooltip';
import {
  NpsFilterChips,
  generateFilterChips,
} from './components/NpsFilterChips';
import SaveAltIcon from '@material-ui/icons/SaveAlt';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  contactsColumn: {
    textAlign: 'left',
  },
  clickable: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    textAlign: 'left',
  },
  accordionSummaryFocused: {
    outline: `1px solid #efefef`,
  },
  boldTitle: {
    fontWeight: 600,
    padding: 0,
    textAlign: 'left',
    clear: 'both',
    display: 'block',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  personDivider: {
    margin: '4px 0',
    borderTop: '1px solid #eaeaea',
    width: '100%',
  },
  dateColumn: {
    width: 110,
    textAlign: 'center',
  },
  ratingColumn: {
    width: 100,
    textAlign: 'center',
  },
  viewColumn: {
    width: 60,
    textAlign: 'center',
  },
  capitlizeText: {
    textTransform: 'capitalize',
  },
  tagChip: {
    textTransform: 'capitalize',
    margin: theme.spacing(0.5),
  },
  commentContent: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: grey[200],
    padding: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&::before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      minHeight: 'inherit',
    },
  },
  accordionDropdown: {
    padding: '0px',
  },
  cardHeader: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& .MuiCardHeader-action': {
        marginRight: 0,
        marginTop: theme.spacing(2),
        alignSelf: 'stretch',
      },
    },
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  cardTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  cardSubheader: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  badgeContentWhite: {
    color: 'white',
  },
  candidateNameContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover $copyIcon': {
      opacity: 1,
    },
  },
  copyIcon: {
    opacity: 0,
    marginLeft: theme.spacing(0.5),
    cursor: 'pointer',
    fontSize: '1rem',
    color: theme.palette.grey[600],
    transition: 'opacity 0.2s ease-in-out',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  lastUpdatedContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  statusCircle: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
}));

interface Props {
  filteredCompanyId?: string;
  candidateUserId?: string;
}

export const NpsView: React.FC<Props> = ({ filteredCompanyId }) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<PagedNpsResponse | undefined>();
  const [lastUpdated, setLastUpdated] = React.useState<
    NpsLastUpdated | undefined
  >();
  const isStale = lastUpdated
    ? differenceInHours(
        new Date(),
        new Date(lastUpdated.last_execution_time),
      ) >= 5
    : false;
  const [leadersLoading, setLeadersLoading] = React.useState(false);
  const [leadersData, setLeadersData] = React.useState<
    NpsLeader[] | undefined
  >();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const candidateUserId = queryParams.get('candidateId') ?? undefined;

  const classes = useStyles();
  const [viewMoreDialogOpen, setViewMoreDialogOpen] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState<Job[]>();
  const [pageNumber, setPageNumber] = React.useState(0);

  const [exporting, setExporting] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = React.useState(false);

  useDocumentTitle('Net Promoter Score');

  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const defaultDateFrom = candidateUserId
    ? undefined
    : startOfMonth(subMonths(new Date(), 3));
  const defaultDateTo = new Date();

  const [submissionStatus, setSubmissionStatus] = React.useState<
    string | undefined
  >('Submitted');
  const [ratingType, setRatingType] = React.useState<string[]>([]);
  const [tierType, setTierType] = React.useState<string | undefined>(undefined);
  const [userType, setUserType] = React.useState<string[]>([]);
  const [brand, setBrand] = React.useState<string[]>([]);
  const [showTtv, setShowTtv] = React.useState(false);
  const [showCommentRecords, setShowCommentRecords] = React.useState(false);
  const [showJiraRecords, setShowJiraRecords] = React.useState(false);
  const [actionBy, setActionBy] = React.useState<string | undefined>(undefined);
  const [actionNeeded, setActionNeeded] = React.useState<string | undefined>(
    undefined,
  );
  const [reviewee, setReviewee] = React.useState<string[]>([]);
  const [team, setTeam] = React.useState<string[]>([]);
  const [submissionType, setSubmissionType] = React.useState<
    string | undefined
  >(undefined);
  const [dateHandled, setdateHandled] = React.useState<string | undefined>(
    undefined,
  );

  const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
    defaultDateFrom,
  );
  const [dateTo, setDateTo] = React.useState<Date>(defaultDateTo);

  const [textSearch, setTextSearch] = React.useState<string | undefined>(
    undefined,
  );
  const debouncedTextSearch = useDebounce(textSearch, 400);

  const history = useHistory();
  const { npsId } = useParams<{ npsId?: string }>();
  // The submission dialog is driven by the URL so a specific NPS submission can
  // be deep-linked / shared: /nps (list), /nps/new (create), /nps/:id (edit).
  const npsDialogOpen = Boolean(npsId);
  const selectedNpsResponseId = npsId && npsId !== 'new' ? npsId : undefined;
  const [tagsDialogOpen, setTagsDialogOpen] = React.useState(false);

  const handleViewMoreDialog = async (jobId: string) => {
    setDialogDataLoading(true);
    const jobDetails = await fetchJobDetails(jobId);
    setSelectedJob(jobDetails);
    setViewMoreDialogOpen(true);
    setDialogDataLoading(false);
  };

  const [dialogDataLoading, setDialogDataLoading] = React.useState(false);

  const fetchData = React.useCallback(() => {
    let pageNumberToLoader = pageNumber + 1;
    setLoading(true);
    const dateFromParam = !dateFrom
      ? undefined
      : format(startOfDay(dateFrom), DateTimeFormat.INPUT);
    const dateToParam = format(endOfDay(dateTo), DateTimeFormat.INPUT);
    fetchNpsResponses(
      pageNumberToLoader,
      tableSettings.rowsPerPage,
      debouncedTextSearch,
      submissionStatus,
      ratingType,
      userType,
      brand,
      reviewee,
      dateFromParam,
      dateToParam,
      filteredCompanyId,
      dateHandled !== 'All' ? dateHandled : undefined,
      actionNeeded,
      actionBy,
      team,
      submissionType,
      tierType,
      showTtv,
      candidateUserId,
      showCommentRecords,
      showJiraRecords,
    ).then((d) => {
      setLoading(false);
      setData(d);
    });
  }, [
    setLoading,
    setData,
    pageNumber,
    tableSettings.rowsPerPage,
    debouncedTextSearch,
    dateFrom,
    dateTo,
    brand,
    reviewee,
    submissionStatus,
    ratingType,
    userType,
    filteredCompanyId,
    dateHandled,
    actionNeeded,
    actionBy,
    submissionType,
    tierType,
    team,
    showTtv,
    candidateUserId,
    showCommentRecords,
    showJiraRecords,
  ]);

  const exportData = React.useCallback(async () => {
    setExporting(true);
    setSnackbarOpen(true);
    const dateFromParam = !dateFrom
      ? undefined
      : format(startOfDay(dateFrom), DateTimeFormat.INPUT);
    const dateToParam = format(endOfDay(dateTo), DateTimeFormat.INPUT);
    await exportNpsResponses(
      debouncedTextSearch,
      submissionStatus,
      ratingType,
      userType,
      brand,
      reviewee,
      dateFromParam,
      dateToParam,
      filteredCompanyId,
      dateHandled,
      actionNeeded,
      actionBy,
      team,
      submissionType,
      tierType,
      showTtv,
      candidateUserId,
      showCommentRecords,
      showJiraRecords,
    );
    setExporting(false);
  }, [
    debouncedTextSearch,
    dateFrom,
    dateTo,
    brand,
    reviewee,
    submissionStatus,
    ratingType,
    userType,
    filteredCompanyId,
    dateHandled,
    actionNeeded,
    actionBy,
    team,
    submissionType,
    tierType,
    showTtv,
    candidateUserId,
    showCommentRecords,
    showJiraRecords,
  ]);

  const user = getUserData();

  const fetchLeaders = React.useCallback(() => {
    setLeadersLoading(true);
    fetchNpsLeaders().then((d) => {
      setLeadersLoading(false);
      setLeadersData(d);
    });
  }, [setLeadersLoading, setLeadersData]);

  React.useEffect(() => {
    fetchLastUpdated().then((d) => {
      setLastUpdated(d);
    });
  }, []);

  React.useEffect(() => {
    fetchData();
    fetchLeaders();
  }, [fetchData, fetchLeaders]);

  const columns: PageableTableHeader[] = [
    {
      key: 'rating',
      label: 'Rating',
      sortable: false,
      props: {
        className: classes.ratingColumn,
      },
    },
    {
      key: 'type',
      label: 'User Type',
      sortable: false,
    },
    {
      key: 'contact',
      label: 'Contact',
      sortable: false,
      props: {
        className: classes.contactsColumn,
      },
    },
    {
      key: 'product',
      label: 'Product',
      sortable: false,
    },
    {
      key: 'reviewee',
      label: 'Reviewee',
      sortable: false,
    },
    {
      key: 'dateSent',
      label: 'Sent',
      sortable: false,
      props: {
        className: classes.dateColumn,
      },
    },
    {
      key: 'dateSubmitted',
      label: 'Submitted',
      sortable: false,
      props: {
        className: classes.dateColumn,
      },
    },
    {
      key: 'comment',
      label: 'View Details',
      sortable: false,
      props: { className: classes.ratingColumn },
    },
  ];

  const revieweeOptions = leadersData ?? [];

  const filterChips = generateFilterChips({
    textSearch,
    submissionStatus,
    ratingType,
    userType,
    brand,
    reviewee,
    dateFrom,
    dateTo,
    dateHandled,
    actionNeeded,
    actionBy,
    team,
    submissionType,
    tierType,
    showTtv,
    showCommentRecords,
    showJiraRecords,
    setters: {
      setTextSearch,
      setSubmissionStatus,
      setRatingType,
      setUserType,
      setBrand,
      setReviewee,
      setDateFrom,
      setDateTo,
      setdateHandled,
      setActionNeeded,
      setActionBy,
      setTeam,
      setSubmissionType,
      setTierType,
      setShowTtv,
      setShowCommentRecords,
      setShowJiraRecords,
    },
  });

  const [filterAccordionExpanded, setFilterAccordionExpanded] =
    React.useState(true);

  const handleTextSearchChange = (value: string | undefined) => {
    setTextSearch(value);
  };

  const handleSubmissionStatusChange = (value: string | undefined) => {
    setSubmissionStatus(value);
  };

  const handleRatingTypeChange = (values: string[]) => {
    setRatingType(values);
  };

  const handleUserTypeChange = (values: string[]) => {
    setUserType(values);
  };

  const handleBrandChange = (values: string[]) => {
    setBrand(values);
  };

  const handleRevieweeChange = (values: string[]) => {
    setReviewee(values);
  };

  const handleDateFromChange = (value: Date | undefined) => {
    setDateFrom(value);
  };

  const handleDateToChange = (date: Date) => {
    setDateTo(date);
  };

  const handleDateHandledChange = (value: string | undefined) => {
    setdateHandled(value);
  };

  const handleActionNeededChange = (value: string | undefined) => {
    setActionNeeded(value);
  };

  const handleActionByChange = (value: string | undefined) => {
    setActionBy(value);
  };

  const handleTeamChange = (values: string[]) => {
    setTeam(values);
  };

  const handleSubmissionTypeChange = (value: string | undefined) => {
    setSubmissionType(value);
  };

  const handleTierTypeChange = (value: string | undefined) => {
    setTierType(value);
  };

  const handleShowTtvChange = (checked: boolean) => {
    setShowTtv(checked);
  };

  const handleShowCommentRecordsChange = (checked: boolean) => {
    setShowCommentRecords(checked);
  };

  const clearAllFilters = () => {
    setTextSearch(undefined);
    setSubmissionStatus('Submitted');
    setRatingType([]);
    setUserType([]);
    setBrand([]);
    setReviewee([]);
    setdateHandled(undefined);
    setActionNeeded(undefined);
    setActionBy(undefined);
    setTeam([]);
    setSubmissionType(undefined);
    setTierType(undefined);
    setShowTtv(false);
    setShowCommentRecords(false);
    setShowJiraRecords(false);

    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
  };

  const handleCopyUserInfo = (
    userName: string,
    userId: string,
    brandIdentifier: string,
    userType: string,
  ) => {
    let url = '';
    if (brandIdentifier === 'knt') {
      if (userType === 'candidate' || userType === 'candidate-executive') {
        url = `https://konnekt.talexiohr.com/ats/candidates/${userId}`;
      } else if (userType === 'client') {
        url = `https://konnekt.talexiohr.com/ats/companies/${userId}`;
      }
    }

    const textToCopy = `${userName}\n${url}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch(() => {
        setCopySnackbarOpen(true);
      });
  };

  const hasActiveFilters =
    textSearch !== undefined ||
    submissionStatus !== undefined ||
    ratingType.length > 0 ||
    userType.length > 0 ||
    brand.length > 0 ||
    reviewee.length > 0 ||
    dateFrom !== undefined ||
    dateHandled !== undefined ||
    actionNeeded !== undefined ||
    actionBy !== undefined ||
    team.length > 0 ||
    submissionType !== undefined ||
    tierType !== undefined ||
    showTtv ||
    showCommentRecords ||
    showJiraRecords;

  return (
    <Card className={classes.card}>
      <CardHeader
        classes={{
          root: classes.cardHeader,
          title: classes.cardTitle,
          subheader: classes.cardSubheader,
        }}
        title={candidateUserId ? '' : 'Net Promoter Score Submissions'}
        subheader={
          candidateUserId ? (
            ''
          ) : (
            <>
              Submissions from all KonnektTalexio NPS surveys.
              <div className={classes.lastUpdatedContainer}>
                <div
                  className={classes.statusCircle}
                  style={{
                    backgroundColor: isStale ? red[500] : green[500],
                  }}
                />
                <span>
                  Last Updated:{' '}
                  {lastUpdated ? (
                    format(
                      new Date(lastUpdated.last_execution_time),
                      'dd/MM/yyyy HH:mm',
                    )
                  ) : (
                    <Skeleton
                      variant="text"
                      width={120}
                      style={{ display: 'inline-block' }}
                    />
                  )}
                </span>
              </div>
            </>
          )
        }
        action={
          <div className={classes.headerActions}>
            {!candidateUserId && (
              <>
                <Button
                  color="secondary"
                  href="/dashboards#nps"
                  target="_blank"
                  startIcon={<LaunchIcon />}
                >
                  NPS Dashboard
                </Button>
                <Button
                  color="secondary"
                  onClick={exportData}
                  disabled={exporting}
                  startIcon={<SaveAltIcon />}
                >
                  Export Data
                  {exporting && (
                    <Box ml={1}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Button>
                {user?.permissions?.includes('nps') && (
                  <Button
                    color="secondary"
                    onClick={() => {
                      setTagsDialogOpen(true);
                    }}
                  >
                    Tag Manager
                  </Button>
                )}

                {user?.permissions?.includes('nps') && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => history.push('/nps/new')}
                  >
                    Create Submission
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {!candidateUserId && (
        <Accordion
          className={classes.accordion}
          elevation={0}
          expanded={filterAccordionExpanded}
          onChange={(_, expanded) => setFilterAccordionExpanded(expanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon style={{ marginRight: 16 }} />}
            className={classes.accordionSummaryFocused}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ paddingLeft: 16 }}>
                  Filters
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <CardContent style={{ paddingTop: 0, paddingBottom: 8 }}>
              <Grid container spacing={1}>
                {!filteredCompanyId && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    lg={12}
                    style={{ marginBottom: '16px' }}
                  >
                    <TextField
                      value={textSearch}
                      variant="outlined"
                      autoFocus
                      onChange={(e) =>
                        handleTextSearchChange(
                          e.currentTarget.value || undefined,
                        )
                      }
                      fullWidth
                      placeholder="Type here to search by company, respondent, email, or phone number"
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl className={classes.formControl} fullWidth>
                    <DatePicker
                      label={'Date from'}
                      margin="none"
                      fullWidth
                      value={dateFrom}
                      inputVariant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={(e: MaterialUiPickersDate) => {
                        handleDateFromChange(e ?? undefined);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl className={classes.formControl} fullWidth>
                    <DatePicker
                      label={'Date to'}
                      margin="none"
                      fullWidth
                      value={dateTo}
                      inputVariant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={(e: MaterialUiPickersDate) => {
                        if (!e) {
                          return;
                        }
                        handleDateToChange(e);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      options={['Submitted', 'Unsubmitted']}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Submission Status"
                          placeholder="All Submissions"
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      value={submissionStatus}
                      onChange={(e, newValue) => {
                        handleSubmissionStatusChange(newValue || undefined);
                      }}
                    />
                  </FormControl>
                </Grid>
                {!filteredCompanyId && (
                  <Grid item xs={12} sm={3} lg={3}>
                    <FormControl
                      className={classes.formControl}
                      variant="outlined"
                      fullWidth
                    >
                      <Autocomplete
                        options={['Yes', 'No']}
                        renderInput={(params) => (
                          <TextField
                            variant="outlined"
                            label="Handled"
                            {...params}
                            fullWidth
                            placeholder="All Submissions"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        disableClearable={false}
                        value={dateHandled}
                        onChange={(e, newValue) => {
                          handleDateHandledChange(newValue || undefined);
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      multiple
                      options={[
                        'Admin',
                        'Candidate',
                        'Candidate-Executive',
                        'Client',
                        'Non-Admin',
                      ]}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="User Type"
                          {...params}
                          fullWidth
                          placeholder="All user types"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={true}
                      value={userType}
                      onChange={(e, newValue) => {
                        handleUserTypeChange(newValue);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl className={classes.formControl} fullWidth>
                    <Autocomplete
                      multiple
                      options={brandList}
                      getOptionLabel={(o) => o.label}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Brand"
                          {...params}
                          fullWidth
                          placeholder="All brands"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={true}
                      value={brandList.filter((b) =>
                        brand.includes(b.brand_identifier),
                      )}
                      onChange={(e, newValues) => {
                        handleBrandChange(
                          newValues.map((v) => v.brand_identifier),
                        );
                      }}
                    />
                  </FormControl>
                </Grid>
                {!filteredCompanyId && (
                  <Grid item xs={12} sm={4} lg={3}>
                    <FormControl className={classes.formControl} fullWidth>
                      <Autocomplete
                        multiple
                        options={[
                          'Generalist',
                          'Candidate Engagement',
                          'Recruitment Projects',
                          'Tech',
                          'Finance and Legal',
                          'KonnektTalexio Cyprus',
                        ]}
                        renderInput={(params) => (
                          <TextField
                            variant="outlined"
                            label="Team"
                            {...params}
                            fullWidth
                            placeholder="All teams"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        disableClearable={true}
                        value={team}
                        onChange={(e, newValue) => {
                          handleTeamChange(newValue);
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
                {!filteredCompanyId && (
                  <Grid item xs={12} sm={4} lg={3}>
                    <FormControl className={classes.formControl} fullWidth>
                      <Autocomplete
                        multiple
                        loading={leadersLoading}
                        getOptionLabel={(o) =>
                          `${o.reviewee}${
                            o.reviewee !== 'All'
                              ? ` (${o.team_identifier})`
                              : ''
                          }`
                        }
                        options={revieweeOptions}
                        renderInput={(params) => (
                          <TextField
                            variant="outlined"
                            label="Recruiter / Executive"
                            {...params}
                            fullWidth
                            placeholder="All reviewees"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        value={revieweeOptions.filter((r) =>
                          reviewee.includes(r.reviewee),
                        )}
                        onChange={(e, newValue) => {
                          handleRevieweeChange(newValue.map((v) => v.reviewee));
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sm={3} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      options={['Yes', 'No', 'Missing Tags']}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Action Needed"
                          {...params}
                          fullWidth
                          placeholder="All Action Types"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={false}
                      value={actionNeeded}
                      onChange={(e, newValue) => {
                        handleActionNeededChange(newValue || undefined);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      getOptionLabel={(o) =>
                        `${o.reviewee}${
                          o.reviewee !== 'All' ? ` (${o.team_identifier})` : ''
                        }`
                      }
                      options={revieweeOptions}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Action By"
                          {...params}
                          fullWidth
                          placeholder="All reviewees"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={true}
                      value={revieweeOptions.find(
                        (f) => f.reviewee === actionBy,
                      )}
                      onChange={(e, newValue) => {
                        handleActionByChange(newValue?.reviewee);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      options={['Manual Submission', 'User Submission']}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Submission Type"
                          {...params}
                          fullWidth
                          placeholder="All submission types"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={false}
                      value={submissionType || null}
                      onChange={(e, newValue) => {
                        handleSubmissionTypeChange(newValue || undefined);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} lg={3}>
                  <FormControl
                    className={classes.formControl}
                    variant="outlined"
                    fullWidth
                  >
                    <Autocomplete
                      multiple
                      options={['Promoter', 'Neutral', 'Detractor']}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Rating Type"
                          placeholder="All Rating types"
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      disableClearable={true}
                      value={ratingType}
                      onChange={(e, newValue) => {
                        handleRatingTypeChange(newValue);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showCommentRecords}
                        onChange={(e, checked) => {
                          handleShowCommentRecordsChange(checked);
                        }}
                      />
                    }
                    label={'Only show records with comments'}
                  />
                </Grid>
                <Grid item sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showJiraRecords}
                        onChange={(e, checked) => setShowJiraRecords(checked)}
                      />
                    }
                    label={'Only show records with a Jira ticket'}
                  />
                </Grid>
                {(brand.includes('tlx') || brand.includes('talexio')) && (
                  <Grid item sm={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showTtv}
                          onChange={(e, checked) => {
                            handleShowTtvChange(checked);
                          }}
                        />
                      }
                      label={'Only show Team Voice Submissions'}
                    />
                  </Grid>
                )}
                {(brand.includes('tlx') || brand.includes('talexio')) && (
                  <Grid item sm={3}>
                    <Autocomplete
                      options={['Free Users', 'Paid Users']}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          label="Subscription Type"
                          placeholder="All Subscription types"
                          {...params}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      value={tierType || null}
                      onChange={(e, newValue) => {
                        handleTierTypeChange(newValue || undefined);
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3} lg={3}>
                  <Button
                    variant="outlined"
                    onClick={clearAllFilters}
                    startIcon={<ClearAllIcon />}
                    disabled={!hasActiveFilters}
                  >
                    Clear All Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </AccordionDetails>
        </Accordion>
      )}

      {!candidateUserId && !filterAccordionExpanded && (
        <NpsFilterChips filters={filterChips} />
      )}

      <PageableTable
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        showSkeletonLoading
        rows={
          data?.data?.map((d, index) => ({
            key: index.toString(),
            cells: [
              {
                key: 'rating',
                props: {
                  className: classes.ratingColumn,
                },
                display: (
                  <div
                    style={
                      d.rating_type
                        ? {
                            color:
                              d.rating_type === 'promoter'
                                ? green[600]
                                : d.rating_type === 'detractor'
                                ? red[600]
                                : 'inherit',
                          }
                        : undefined
                    }
                  >
                    {d.rating !== null && (
                      <Typography variant="h6" style={{ lineHeight: 1 }}>
                        {d.rating}
                      </Typography>
                    )}
                    {d.rating_type && (
                      <Typography
                        variant="caption"
                        className={classes.capitlizeText}
                      >
                        {d.rating_type}
                      </Typography>
                    )}
                  </div>
                ),
              },
              {
                key: 'type',
                display: (
                  <React.Fragment>
                    {d.user_type && (
                      <Typography
                        variant="body1"
                        className={classes.capitlizeText}
                      >
                        {d.user_type}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      className={classes.capitlizeText}
                    >
                      {resolvePlatformName(d.brand_identifier ?? '').replaceAll(
                        'Konnekt ATS',
                        'Konnekt',
                      )}
                    </Typography>
                  </React.Fragment>
                ),
              },
              {
                key: 'contact',
                display: (
                  <React.Fragment>
                    <div className={classes.candidateNameContainer}>
                      <Button
                        className={classNames(
                          d.user_id ? [classes.boldTitle] : [classes.boldTitle],
                        )}
                        variant="text"
                        style={{ textAlign: 'left' }}
                        onClick={() => {
                          if (d.user_id) {
                            if (d.brand_identifier === 'knt') {
                              if (
                                d.user_type === 'candidate' ||
                                d.user_type === 'candidate-executive'
                              ) {
                                window.open(
                                  `https://konnekt.talexiohr.com/ats/candidates/${d.user_id}`,
                                );
                              } else if (d.user_type === 'client') {
                                window.open(
                                  `https://konnekt.talexiohr.com/ats/companies/${d.user_id}`,
                                );
                              }
                            }
                          }
                        }}
                      >
                        {d.user_first_name} {d.user_last_name}
                      </Button>
                      {d.user_first_name && d.user_last_name && (
                        <FileCopyIcon
                          className={classes.copyIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyUserInfo(
                              `${d.user_first_name} ${d.user_last_name}`,
                              String(d.user_id!),
                              d.brand_identifier || '',
                              d.user_type || '',
                            );
                          }}
                        />
                      )}
                    </div>

                    {d.user_designation && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        component="div"
                      >
                        {d.user_designation}
                      </Typography>
                    )}

                    {d.company && (
                      <div>
                        <div className={classes.personDivider}></div>
                        <Button
                          className={classNames(
                            d.company_id
                              ? [classes.boldTitle]
                              : [classes.boldTitle],
                          )}
                          variant="text"
                          onClick={
                            d.company_id
                              ? () => {
                                  window.open(`/clients/${d.company_id}`);
                                }
                              : () => {}
                          }
                        >
                          {d.company}
                        </Button>
                        {d.blacklisted && (
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Flag
                              style={{
                                color: 'black',
                                fontSize: '1.5em',
                              }}
                            />
                            <CustomTooltip
                              titleHeader="Listing Reasons"
                              bodyText={d.blacklisted_reasons}
                              placement="top"
                              maxWidth={600}
                              enabledArrow={true}
                            >
                              <Typography
                                variant="caption"
                                style={{
                                  marginLeft: '10px',
                                  borderBottom: '1px dotted',
                                  cursor: 'pointer',
                                }}
                              >
                                Blacklisted by:{' '}
                                {d.blacklisted
                                  .slice(1, -1)
                                  .replace(/'/g, '')
                                  .split(',')
                                  .map((item) => item.trim())
                                  .join(', ')}
                              </Typography>
                            </CustomTooltip>
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ),
              },

              {
                key: 'product',
                display: (
                  <React.Fragment>
                    {(() => {
                      if (d.product_name) {
                        let productName, statusLabel;

                        if (
                          d.product_name.includes('(') &&
                          d.product_name.includes(')')
                        ) {
                          productName = d.product_name.substring(
                            0,
                            d.product_name.lastIndexOf(' ('),
                          );
                          let potentialStatus = d.product_name.substring(
                            d.product_name.lastIndexOf(' (') + 2,
                            d.product_name.length - 1,
                          );
                          const statuses = [
                            'Successful',
                            'Position Shelved',
                            'Unsuccessful',
                          ];
                          statusLabel = statuses.includes(potentialStatus)
                            ? potentialStatus
                            : '';
                        } else {
                          productName = d.product_name
                            .split(',')
                            .filter((name) => name !== 'support')
                            .map((name) =>
                              name
                                .split('_')
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .join(' '),
                            )
                            .join(', ')
                            .replace('Hr ', 'HR ');
                        }

                        let productContent = d.product_id ? (
                          <Button
                            className={classes.boldTitle}
                            onClick={() =>
                              d.product_id && handleViewMoreDialog(d.product_id)
                            }
                            style={{ cursor: 'pointer' }}
                            color="default"
                          >
                            {productName}
                          </Button>
                        ) : (
                          <Typography variant="body1">{productName}</Typography>
                        );

                        return (
                          <React.Fragment>
                            {productContent}
                            {statusLabel && (
                              <Typography variant="caption">
                                {statusLabel}
                              </Typography>
                            )}
                          </React.Fragment>
                        );
                      } else {
                        return <Typography variant="body1">N/A</Typography>;
                      }
                    })()}
                  </React.Fragment>
                ),
              },

              {
                key: 'reviewee',
                display: (
                  <React.Fragment>
                    {d.reviewee ? (
                      <Typography variant="body1">{d.reviewee}</Typography>
                    ) : (
                      <Typography variant="body1">N/A</Typography>
                    )}
                    {d.team_identifier && (
                      <Typography
                        variant="caption"
                        className={classes.capitlizeText}
                      >
                        {d.team_identifier}
                      </Typography>
                    )}
                  </React.Fragment>
                ),
              },
              {
                key: 'dateSent',
                display:
                  d.date_sent && d.date_sent !== 'NaT' ? (
                    <DateDisplay date={d.date_sent} />
                  ) : (
                    <Typography variant="body1">-</Typography>
                  ),
                props: {
                  className: classes.dateColumn,
                },
              },
              {
                key: 'dateSubmitted',
                display: d.date_submitted && d.date_submitted !== 'NaT' && (
                  <DateDisplay date={d.date_submitted} />
                ),
                props: {
                  className: classes.dateColumn,
                },
              },
              {
                key: 'comment',
                display: (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Badge
                      badgeContent={
                        (d.comment_count || 0) >= 1 ? d.comment_count : 0
                      }
                      color={
                        (d.comment_count || 0) >= 1 ? 'secondary' : 'default'
                      }
                      classes={{ badge: classes.badgeContentWhite }}
                    >
                      <RemoveRedEye
                        onClick={() => {
                          if (d.platform_nps_id) {
                            history.push(
                              `/nps/${encodeURIComponent(d.platform_nps_id)}`,
                            );
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          color:
                            (d.comment_count || 0) >= 1 ? 'primary' : 'inherit',
                        }}
                      >
                        <Comment />
                      </RemoveRedEye>
                    </Badge>
                  </Box>
                ),
              },
            ],
          })) ?? []
        }
        loading={loading}
        pageNumber={pageNumber}
        rowCount={data?.total_count ?? 0}
        columns={columns}
        onChangePage={setPageNumber}
      />
      <NpsDialog
        open={npsDialogOpen}
        defaultCompanyId={filteredCompanyId}
        selectedNpsResponseId={selectedNpsResponseId}
        onClose={() => history.push('/nps')}
      />
      <TagsDialog
        open={tagsDialogOpen}
        onClose={() => {
          setTagsDialogOpen(false);
        }}
      />
      <JobDetailsDialog
        open={viewMoreDialogOpen}
        job={selectedJob!}
        loading={dialogDataLoading}
        onClose={() => setViewMoreDialogOpen(false)}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          Exporting data, your download will start shortly.
        </Alert>
      </Snackbar>
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
      >
        <Alert onClose={() => setCopySnackbarOpen(false)} severity="success">
          User information copied to clipboard.
        </Alert>
      </Snackbar>
    </Card>
  );
};
